import { Component, OnInit } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faTachometerAlt, 
  faUsers, 
  faFileAlt, 
  faCalendarAlt, 
  faBell, 
  faSync,
  faSignOutAlt,
  faBuilding,
  faIdCard,
  faUserPlus,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { filter, tap } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { Observable, of, catchError } from 'rxjs';
import { AuthService } from '../../../../services/auth/auth.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-human-resources',
  standalone: true,
  imports: [RouterModule, CommonModule, FontAwesomeModule],
  templateUrl: './human-resources.component.html',
  styleUrls: ['./human-resources.component.scss']
})
export class HumanResourcesComponent implements OnInit {
  // FontAwesome icons
  faTachometerAlt = faTachometerAlt;
  faUsers = faUsers;
  faFileAlt = faFileAlt;
  faIdCard = faIdCard;
  faCalendarAlt = faCalendarAlt;
  faBell = faBell;
  faSync = faSync;
  faSignOutAlt = faSignOutAlt;
  faBuilding = faBuilding;
  faUserPlus = faUserPlus;
  faVideo = faVideo;
  // Current route
  currentRoute: string = '';
  
  // Company information
  companyName$: Observable<string>;
  companyInitials$: Observable<string>;
  
  // Superuser flag
  public isSuperuser: boolean = false;
  public userName: string = localStorage.getItem('username') || '';
  // Debugging
  isLoading = true;
  public  address = environment.address;
  constructor(
    private router: Router,
    private companyService: CompanyService,
    private authService: AuthService
  ) {
    console.log('HumanResourcesComponent initialized');
    
    // Add fallback for company name and initials
    this.companyName$ = this.getCompanyName();
    this.companyInitials$ = this.getCompanyInitials();
      
    // Log authentication status
    console.log('Auth token present:', !!localStorage.getItem('auth_token'));
    
    // Check if user is a superuser
    this.checkSuperuserStatus();
  }

  ngOnInit() {
    console.log('HumanResourcesComponent ngOnInit');
    
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      // Extract the last part of the URL to get the current route
      const urlParts = event.urlAfterRedirects.split('/');
      this.currentRoute = urlParts[urlParts.length - 1];
      console.log('Current route changed to:', this.currentRoute);
    });
  }
  
  /**
   * Check if the current user is a superuser
   */
  private checkSuperuserStatus(): void {
    // First check the synchronous method for an immediate result
    this.isSuperuser = this.authService.isSuperuser();
    console.log('Initial superuser check (local):', this.isSuperuser);
    
    // Then make the API call to confirm
    this.authService.checkSuperuser().subscribe(isSuperuser => {
      this.isSuperuser = isSuperuser;
      console.log('Superuser check (from API):', this.isSuperuser);
    });
  }
  
  /**
   * Get company name with debug logging
   */
  private getCompanyName(): Observable<string> {
    console.log('Getting company name in component');
    return this.companyService.getCompanyName()
      .pipe(
        tap(name => {
          console.log('Company name retrieved:', name);
          this.isLoading = false;
        }),
        catchError(error => {
          console.error('Error getting company name in component:', error);
          this.isLoading = false;
          return of('HR Management');
        })
      );
  }
  
  /**
   * Get company initials with debug logging
   */
  private getCompanyInitials(): Observable<string> {
    console.log('Getting company initials in component');
    return this.companyService.getCompanyInitials()
      .pipe(
        tap(initials => console.log('Company initials retrieved:', initials)),
        catchError(error => {
          console.error('Error getting company initials in component:', error);
          return of('HR');
        })
      );
  }
  
  /**
   * Force refresh company information
   */
  refreshCompanyInfo() {
    console.log('Manually refreshing company info');
    this.isLoading = true;
    
    // Clear the service cache
    this.companyService.clearCache();
    
    // Refresh the observables
    this.companyName$ = this.getCompanyName();
    this.companyInitials$ = this.getCompanyInitials();
  }

  /**
   * Logout user and redirect to login page
   */
  logout() {
    console.log('Logging out user');
    this.authService.logout();
    this.router.navigate(['/hr/login']);
  }

  navigateToProfile() {
    this.router.navigate(['/hr/profile']);
  }
}
